import React, { useEffect, useRef, useState } from "react";
import { Trash2, Plus, ImagePlus, Save, ArrowUp, ArrowDown } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { HomeAdView, HomeAdEditBox, lineVisible, AD_FONTS, AD_ANIMS, fontStyle } from "@/components/HomeAd";

const PALETTE = ["#FFFFFF", "#000000", "#F8FAFC", "#94A3B8", "#EF4444", "#DC2626", "#F97316", "#FB923C", "#FACC15", "#FDE047", "#84CC16", "#22C55E", "#10B981", "#14B8A6", "#06B6D4", "#0EA5E9", "#3B82F6", "#1D4ED8", "#6366F1", "#8B5CF6", "#A855F7", "#D946EF", "#EC4899", "#F43F5E", "#92400E", "#D4AF37", "#C0C0C0", "#7F1D1D"];
import { removeBackground } from "@/lib/removeBg";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
function status(s) {
  const t = today();
  if (s.end && s.end < t) return { label: "Skaduar", cls: "bg-red-50 text-red-600" };
  if (s.start && s.start > t) return { label: "Planifikuar", cls: "bg-amber-50 text-amber-700" };
  return { label: "Aktive", cls: "bg-emerald-50 text-emerald-700" };
}

function SlideEditor({ s, i, total, onChange, onDelete, onMove }) {
  const st = status(s);
  const set = (k, v) => onChange({ ...s, [k]: v });
  const setLine = (j, k, v) => set("lines", s.lines.map((l, x) => (x === j ? { ...l, [k]: v } : l)));
  const [upJ, setUpJ] = useState(null);
  const lineImg = async (j, e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setUpJ(j);
    try { const fd = new FormData(); fd.append("file", f); const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setLine(j, "image", r.data.url); }
    catch (err) { toast.error(errMsg(err)); } finally { setUpJ(null); }
  };
  const cutBg = async (j) => {
    const l = s.lines[j];
    setUpJ(j);
    try {
      const blob = await removeBackground(l.image);
      const fd = new FormData(); fd.append("file", new File([blob], "logo.png", { type: "image/png" }));
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("lines", s.lines.map((x, k) => (k === j ? { ...x, image: r.data.url, orig: x.orig || x.image } : x)));
      toast.success("Sfondi u hoq");
    } catch (err) { toast.error("Sfondi nuk u hoq"); } finally { setUpJ(null); }
  };
  const undoBg = (j) => set("lines", s.lines.map((x, k) => (k === j ? { ...x, image: x.orig, orig: undefined } : x)));
  return (
    <div data-testid={`homead-slide-${i}`} className="bg-white rounded-2xl border border-slate-100 p-3 space-y-3">
      <div className="flex gap-3">
        <img src={s.image} alt="" className="w-24 h-24 rounded-xl object-cover shrink-0" />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-slate-800 flex items-center gap-1.5">Foto {i + 1}<span data-testid={`homead-status-${i}`} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${st.cls}`}>{st.label}</span></span>
            <div className="flex items-center gap-1">
              <button type="button" data-testid={`homead-up-${i}`} disabled={i === 0} onClick={() => onMove(-1)} className="p-1.5 text-slate-500 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
              <button type="button" data-testid={`homead-down-${i}`} disabled={i === total - 1} onClick={() => onMove(1)} className="p-1.5 text-slate-500 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
              <button type="button" data-testid={`homead-del-${i}`} onClick={onDelete} className="p-1.5 text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
          <Input data-testid={`homead-link-${i}`} value={s.link} onChange={(e) => set("link", e.target.value)} placeholder="Linku (p.sh. /shpallje/... ose https://...)" className="rounded-xl h-9 text-sm" />
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <Switch data-testid={`homead-linkon-${i}`} checked={s.link_on} onCheckedChange={(v) => set("link_on", v)} />
            {s.link_on ? "Hapet kur preket" : "Nuk hapet"}
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[10px] font-semibold text-slate-500">Fillon<input type="date" data-testid={`homead-start-${i}`} value={s.start || ""} onChange={(e) => set("start", e.target.value)} className="mt-0.5 w-full h-9 rounded-lg border border-slate-200 px-2 text-xs text-slate-800" /></label>
            <label className="text-[10px] font-semibold text-slate-500">Mbaron<input type="date" data-testid={`homead-end-${i}`} value={s.end || ""} min={s.start || undefined} onChange={(e) => set("end", e.target.value)} className="mt-0.5 w-full h-9 rounded-lg border border-slate-200 px-2 text-xs text-slate-800" /></label>
          </div>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
            Teksti qëndron
            <input type="number" min={2} max={120} data-testid={`homead-stay-${i}`} value={s.stay ?? 10} onChange={(e) => set("stay", Math.max(2, Math.min(120, Number(e.target.value) || 10)))} className="w-16 h-9 rounded-lg border border-slate-200 px-2 text-sm text-slate-800" />
            sekonda
          </label>
        </div>
      </div>
      <div className="space-y-2">
        {s.lines.map((l, j) => (
          <div key={j} className="rounded-xl bg-slate-50 p-2 space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-slate-400 w-10 shrink-0">{j % 2 === 0 ? "← maj." : "djath. →"}</span>
            <input type="color" data-testid={`homead-line-color-${i}-${j}`} value={l.color} onChange={(e) => setLine(j, "color", e.target.value)} className="w-9 h-9 rounded-lg border border-slate-200 shrink-0" />
            {l.image ? (
              <div className="flex-1 min-w-0 flex items-center gap-2 h-9 rounded-xl border border-slate-200 px-2" style={{ background: "repeating-conic-gradient(#e2e8f0 0% 25%, #fff 0% 50%) 50% / 12px 12px" }}>
                <img src={l.image} alt="" data-testid={`homead-line-img-${i}-${j}`} className="h-7 max-w-[70px] object-contain" />
                {l.orig
                  ? <button type="button" data-testid={`homead-line-bg-undo-${i}-${j}`} onClick={() => undoBg(j)} className="text-[11px] font-semibold text-slate-600 bg-white/90 rounded-full px-2 py-0.5 shrink-0">Rikthe</button>
                  : <button type="button" data-testid={`homead-line-bg-cut-${i}-${j}`} disabled={upJ === j} onClick={() => cutBg(j)} className="text-[11px] font-semibold text-white bg-emerald-500 rounded-full px-2 py-0.5 shrink-0 disabled:opacity-60">{upJ === j ? "..." : "Hiq sfondin"}</button>}
                <button type="button" data-testid={`homead-line-img-remove-${i}-${j}`} onClick={() => setLine(j, "image", "")} className="ml-auto text-xs font-semibold text-slate-500 underline shrink-0">Tekst</button>
              </div>
            ) : (
              <Input data-testid={`homead-line-text-${i}-${j}`} value={l.text} onChange={(e) => setLine(j, "text", e.target.value)} placeholder={`Rreshti ${j + 1}`} className="rounded-xl h-9 text-sm bg-white" style={{ color: l.color === "#ffffff" || l.color === "#FFFFFF" ? undefined : l.color }} />
            )}
            <label data-testid={`homead-line-img-btn-${i}-${j}`} className="p-1.5 text-emerald-600 cursor-pointer shrink-0" title="Vendos foto">
              {upJ === j ? <span className="text-[10px]">...</span> : <ImagePlus className="w-4 h-4" />}
              <input type="file" accept="image/*" className="hidden" data-testid={`homead-line-img-input-${i}-${j}`} onChange={(e) => lineImg(j, e)} />
            </label>
            <button type="button" data-testid={`homead-line-del-${i}-${j}`} onClick={() => set("lines", s.lines.filter((_, x) => x !== j))} className="p-1.5 text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar pl-12 py-0.5" data-testid={`homead-line-palette-${i}-${j}`}>
            {PALETTE.map((c) => (
              <button type="button" key={c} data-testid={`homead-line-swatch-${i}-${j}-${c.slice(1)}`} onClick={() => setLine(j, "color", c)}
                className={`w-6 h-6 rounded-full border shrink-0 ${String(l.color).toUpperCase() === c ? "ring-2 ring-emerald-500 ring-offset-1" : "border-slate-200"}`} style={{ background: c }} />
            ))}
          </div>
          {!l.image && (
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar pl-12 py-0.5" data-testid={`homead-line-fonts-${i}-${j}`}>
              {AD_FONTS.map((f) => (
                <button type="button" key={f.key} data-testid={`homead-line-font-${i}-${j}-${f.key}`} onClick={() => setLine(j, "font", f.key)}
                  className={`shrink-0 px-3 h-8 rounded-full border text-sm ${(l.font || "bold") === f.key ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-slate-200 text-slate-700"}`}
                  style={fontStyle(f.key)}>{f.label}</button>
              ))}
            </div>
          )}
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar pl-12 py-0.5" data-testid={`homead-line-anims-${i}-${j}`}>
            {AD_ANIMS.filter((a) => !(l.image && a.key === "type")).map((a) => (
              <button type="button" key={a.key} data-testid={`homead-line-anim-${i}-${j}-${a.key}`} onClick={() => setLine(j, "anim", a.key)}
                className={`shrink-0 px-3 h-7 rounded-full border text-xs font-semibold ${(l.anim || "side") === a.key ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-600"}`}>{a.label}</button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-[11px] text-slate-500 pl-12">
            Madhësia
            <input type="range" min={12} max={64} data-testid={`homead-line-size-${i}-${j}`} value={l.size || 24} onChange={(e) => setLine(j, "size", Number(e.target.value))} className="flex-1 accent-emerald-500" />
            <span className="w-8 text-right font-semibold text-slate-700">{l.size || 24}</span>
          </label>
          </div>
        ))}
        <button type="button" data-testid={`homead-line-add-${i}`} onClick={() => set("lines", [...s.lines, { text: "", color: "#FFFFFF", size: 24 }])} className="text-sm font-semibold text-emerald-600 flex items-center gap-1"><Plus className="w-4 h-4" />Shto rresht</button>
      </div>
    </div>
  );
}

export default function AdminHomeAd() {
  const [slides, setSlides] = useState(null);
  const [mode, setMode] = useState("move");
  const [sel, setSel] = useState(0);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { api.get("/admin/home-ad").then((r) => setSlides(r.data.slides)); }, []);
  if (!slides) return <p className="text-center text-slate-400 py-6 text-sm">Duke ngarkuar...</p>;
  const upload = async (e) => {
    const files = [...(e.target.files || [])]; e.target.value = "";
    setBusy(true);
    try {
      const added = [];
      for (const f of files) { const fd = new FormData(); fd.append("file", f); const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); added.push({ image: r.data.url, link: "", link_on: false, start: "", end: "", stay: 10, lines: [{ text: "", color: "#FFFFFF", size: 24 }] }); }
      setSlides([...slides, ...added]);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const move = (i, d) => { const a = [...slides]; [a[i], a[i + d]] = [a[i + d], a[i]]; setSlides(a); };
  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/admin/home-ad", { slides }); setSlides(r.data.slides); toast.success("Reklama në Krye u ruajt"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const preview = slides.map((s) => ({ ...s, lines: s.lines.filter(lineVisible) }));
  const cur = Math.min(sel, slides.length - 1);
  const moveLine = (j, x, y) => setSlides((all) => all.map((s, k) => {
    if (k !== cur) return s;
    const real = s.lines.map((l, idx) => ({ l, idx })).filter((o) => lineVisible(o.l))[j]?.idx;
    return { ...s, lines: s.lines.map((l, idx) => (idx === real ? { ...l, x, y } : l)) };
  }));
  const resetPos = () => setSlides((all) => all.map((s, k) => (k === cur ? { ...s, lines: s.lines.map(({ x, y, ...l }) => l) } : s)));
  return (
    <div className="space-y-3" data-testid="admin-home-ad">
      <p className="text-xs text-slate-500">Kutia katrore në faqen kryesore. Rreshtat hyjnë njëri nga e majta, tjetri nga e djathta, qëndrojnë sa sekonda vendos ti dhe dalin ashtu siç kanë hyrë. Pastaj vjen fotoja tjetër, pa u ndalur kurrë. Me datat "Fillon" dhe "Mbaron" fotoja shfaqet vetëm në ato ditë (bosh = pa afat).</p>
      {slides.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-3" data-testid="homead-preview">
          <div className="flex items-center justify-between mb-2 gap-2">
            <div className="text-xs font-bold text-slate-500">Parashikimi (para se ta ruash)</div>
            <div className="flex bg-slate-100 rounded-lg p-0.5 text-xs font-semibold">
              <button type="button" data-testid="homead-mode-move" onClick={() => setMode("move")} className={`px-2 py-1 rounded-md ${mode === "move" ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500"}`}>Lëviz tekstin</button>
              <button type="button" data-testid="homead-mode-play" onClick={() => setMode("play")} className={`px-2 py-1 rounded-md ${mode === "play" ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500"}`}>Shiko animacionin</button>
            </div>
          </div>
          {mode === "move" ? (
            <>
              {slides.length > 1 && (
                <div className="flex gap-1.5 mb-2 overflow-x-auto no-scrollbar">
                  {slides.map((s, k) => <button key={k} type="button" data-testid={`homead-sel-${k}`} onClick={() => setSel(k)} className={`px-3 py-1 rounded-full text-xs font-semibold shrink-0 ${k === cur ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-600"}`}>Foto {k + 1}</button>)}
                </div>
              )}
              <div className="max-w-[360px] mx-auto"><HomeAdEditBox slide={preview[cur]} onMoveLine={moveLine} /></div>
              <div className="flex items-center justify-between mt-2">
                <p className="text-[11px] text-slate-500">Tërhiq çdo rresht me gisht aty ku do të dalë.</p>
                <button type="button" data-testid="homead-reset-pos" onClick={resetPos} className="text-xs font-semibold text-slate-500 underline shrink-0">Rikthe pozicionet</button>
              </div>
            </>
          ) : (
            <div className="max-w-[360px] mx-auto"><HomeAdView slides={preview} tid="homead-preview-box" inAdmin /></div>
          )}
        </div>
      )}
      {slides.map((s, i) => (
        <SlideEditor key={s.image + i} s={s} i={i} total={slides.length} onChange={(ns) => setSlides(slides.map((x, k) => (k === i ? ns : x)))} onDelete={() => setSlides(slides.filter((_, k) => k !== i))} onMove={(d) => move(i, d)} />
      ))}
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid="homead-img-input" />
      <button type="button" data-testid="homead-img-add" disabled={busy} onClick={() => ref.current?.click()} className="w-full h-11 rounded-xl border-2 border-dashed border-emerald-300 text-emerald-600 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
        <ImagePlus className="w-4 h-4" />{busy ? "Duke punuar..." : "Shto foto"}
      </button>
      <Button data-testid="homead-save" onClick={save} disabled={busy} className="w-full h-11 rounded-xl bg-emerald-500 hover:bg-emerald-600"><Save className="w-4 h-4 mr-1" />Ruaj reklamën</Button>
    </div>
  );
}
